import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Target, ChevronRight, Loader2, AlertCircle, Users, ListTodo
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

export default function DashboardExtras({ onNavigateToTab, showLeads = true }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchExtras = useCallback(async () => {
    try {
      const res = await api.get(token, '/analytics/dashboard-extras');
      setData(res.data);
    } catch (err) {
      console.error('Dashboard extras error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchExtras(); }, [fetchExtras]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-6">
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
      </div>
    );
  }

  if (!data) return null;

  const followUps = data.leadsNeedingFollowup || [];

  return (
    <div className="space-y-4" data-testid="dashboard-extras">
      {/* Quick Stats Strip */}
      <div className={`grid ${showLeads ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
        {showLeads && (
          <button
            onClick={() => onNavigateToTab?.('leads')}
            className="rounded-xl border border-[#E6E0D9] bg-white p-4 text-left hover:border-[#A86A3A]/30 hover:shadow-sm transition-all"
            data-testid="stat-active-leads"
          >
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-emerald-600" />
              <span className="text-xs text-[#8C8078]">Active Leads</span>
            </div>
            <p className="text-2xl font-heading text-[#2C2420]">{data.activeLeads || 0}</p>
          </button>
        )}
        <button
          onClick={() => onNavigateToTab?.('clients')}
          className="rounded-xl border border-[#E6E0D9] bg-white p-4 text-left hover:border-[#A86A3A]/30 hover:shadow-sm transition-all"
          data-testid="stat-total-clients"
        >
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-[#8C8078]">Clients</span>
          </div>
          <p className="text-2xl font-heading text-[#2C2420]">{data.totalClients || 0}</p>
        </button>
        <button
          onClick={() => onNavigateToTab?.('my-tasks')}
          className="rounded-xl border border-[#E6E0D9] bg-white p-4 text-left hover:border-[#A86A3A]/30 hover:shadow-sm transition-all"
          data-testid="stat-overdue-tasks"
        >
          <div className="flex items-center gap-2 mb-1">
            <ListTodo className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-[#8C8078]">Overdue Tasks</span>
          </div>
          <p className={`text-2xl font-heading ${data.overdueTasks > 0 ? 'text-red-600' : 'text-[#2C2420]'}`}>{data.overdueTasks || 0}</p>
        </button>
      </div>

      {/* Leads Needing Follow-up */}
      {showLeads && followUps.length > 0 && (
        <div className="rounded-xl border border-[#E6E0D9] bg-white overflow-hidden" data-testid="leads-followup-section">
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <h3 className="font-heading text-base text-[#2C2420]">Leads Needing Follow-up</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNavigateToTab?.('leads')}
                className="text-xs text-[#A86A3A] h-7"
              >
                View Pipeline <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
          <div className="px-5 pb-4 space-y-2">
            {followUps.map((lead) => (
              <div
                key={lead.id}
                className="flex items-center justify-between p-3 rounded-lg border border-[#E6E0D9] hover:border-[#A86A3A]/30 cursor-pointer transition-all"
                onClick={() => onNavigateToTab?.('leads')}
                data-testid={`followup-lead-${lead.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2C2420] truncate">{lead.name || lead.company || 'Unnamed Lead'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge className="text-[10px] bg-[#F0EBE6] text-[#4A3B32]">{lead.stage}</Badge>
                    {lead.contactName && <span className="text-[10px] text-[#8C8078]">{lead.contactName}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  {lead.value && <p className="text-sm font-medium text-[#2C2420]">${Number(lead.value).toLocaleString()}</p>}
                  {lead.lastContactedAt && (
                    <p className="text-[10px] text-amber-600">
                      Last contact {formatDistanceToNow(parseISO(lead.lastContactedAt), { addSuffix: true })}
                    </p>
                  )}
                  {!lead.lastContactedAt && (
                    <p className="text-[10px] text-red-500">No contact recorded</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
